import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import { StyledTooltip } from '../../commons/utils/TableStyles.js';
import * as Utils from '../../commons/utils/Utils.js';
import InventarioStockGridRow from './InventarioStockGridRow.jsx';
import InventarioStockGridFiltros from './InventarioStockGridFiltros.jsx';
import InventarioStockMovimientosModal from './InventarioStockMovimientosModal.jsx';
import {
  buildInventarioStockFilterFields,
  getInventarioStockFilterDefaults,
} from './inventarioStockFilterFields.js';
import { etiquetaDeUbicacion, parsear } from './inventarioUbicaciones.js';
import { buildEmptyFilterState, serializeFilterState } from '../../commons/filters/GridFilterPanel.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

/**
 * VEC-3313 Scenarios 01, 02 y 03 — Stock por Depósito.
 *
 * Una fila POR ÍTEM, con sus tres columnas de stock calculadas sobre el depósito filtrado (o sobre
 * todos, que es el consolidado). El depósito NO es una columna: es el recorte que define qué
 * significan los números de la fila.
 *
 * LAS COLUMNAS DE STOCK NO SON LO MISMO Y NO SALEN DE UN COUNT:
 *   Disponible = envases en estado DISPONIBLE
 *   Reservado  = envases en estado RESERVADO   ← apartados al abrir el ticket (VEC-3316)
 *   Utilizado  = envases en estado UTILIZADO   ← consumidos al aprobar el presupuesto
 *   Físico     = Disponible + Reservado
 *
 * ⚠️ RESERVADO CUENTA DENTRO DE FÍSICO Y UTILIZADO NO, que es lo que más confunde de esta pantalla:
 * reservar COMPROMETE el envase pero no lo mueve del depósito, así que el físico no cambia y lo que
 * baja es el disponible; utilizarlo sí lo SACA, y ahí el físico baja. La fuente de verdad es
 * `ItemStock::ESTADOS_EN_DEPOSITO` (= DISPONIBLE + RESERVADO), y los tooltips de los encabezados lo
 * dicen, porque ver Físico quieto después de reservar se lee como que la reserva no se guardó.
 *
 * Utilizado existe por una razón concreta: es lo que explica la diferencia entre lo que el depósito
 * llegó a tener y lo que le queda, sin la cual el usuario no puede reconstruir a dónde se fue.
 *
 * Hasta VEC-3316 Reservado era siempre 0 y el tooltip explicaba ESO. Ya no: si vuelve a verse todo
 * en cero con reservas cargadas, es un bug.
 *
 * Molde: `InventarioManoObraGrid` para la grilla (hooks + Utils.getData + las piezas de
 * commons/grid/), pero los filtros van con el panel compartido `GridFilterPanel` y no con el panel
 * escrito a mano de Mano de Obra, que es anterior al refactor de VEC-3690.
 */

const BASE_URL = '/inventario/stock';

const COLUMN_COUNT = 12;

// El default de `GridPagination` es la constante compartida `rowsPerPage` = [10,25,50,100], que NO
// incluye el 5. Se pasa la lista propia para ofrecer las mismas opciones que el resto del módulo
// (Catálogo de Ítems y Mano de Obra); alinearse con la constante es una decisión aparte.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

// El endpoint topea `perPage` en 100 (ItemStockController::PER_PAGE_MAX), así que la última opción
// de la lista es también el máximo que el backend va a respetar.
const PAGINATION_INIT = { page: 0, perPage: 10 };

/**
 * VEC-3316 — EL TIPO DE UBICACIÓN YA NO ES UNA CONSTANTE.
 *
 * Hasta esta card el único tipo era el depósito del Pañol y la grilla lo agregaba fijo. Ahora el
 * desplegable ofrece almacenes Y bases, así que el tipo viaja DENTRO del valor de la opción
 * ("almacen:6", "base:1161") y se parte con `parsear()`. Sigue sin ser un control del usuario, que
 * es el punto: el usuario elige una ubicación, no un modelo de datos.
 *
 * El par se manda COMPLETO o no se manda: el backend rechaza el par a medias con 400
 * (ItemStockService::normalizarUbicacion), y `parsear()` devuelve null tanto para "sin filtro" como
 * para un valor corrupto, así que las dos claves se omiten juntas.
 */

const SORT_QUERY_INIT = {
  numeroDeParte: '',
  // El backend ordena por descripción cuando no le mandan nada (ItemStockController::CAMPO_ORDEN_
  // DEFAULT). Se arranca con lo mismo explícito para que el indicador de la columna coincida con el
  // orden que se está viendo.
  descripcion: 'asc',
  categoria: '',
  subcategoria: '',
  marca: '',
  stockFisico: '',
  stockReservado: '',
  stockUtilizado: '',
  stockDisponible: '',
  stockMinimo: '',
};

/**
 * El query por defecto se calcula con las MISMAS reglas de serialización que aplica el panel, en vez
 * de escribir a mano un objeto con las claves: así no hay dos listas de campos que se desincronicen.
 */
const buildFilterQueryInit = () => {
  const fields = buildInventarioStockFilterFields();
  const estado = Object.assign({}, buildEmptyFilterState(fields), getInventarioStockFilterDefaults());
  return serializeFilterState(fields, estado);
};

const InventarioStockGrid = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  const [searchActive, setSearchActive] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [count, setCount] = useState(0);

  const [filterResetInputs, setFilterResetInputs] = useState(false);
  const [filterQuery, setFilterQuery] = useState(buildFilterQueryInit);

  const [sortQuery, setSortQuery] = useState({ ...SORT_QUERY_INIT });
  const [dropdownActive, setDropdownActive] = useState(false);

  const [rows, setRows] = useState([]);

  // Los depósitos los carga el panel de filtros; la grilla los recibe para poder traducir el id
  // filtrado a su nombre y pasárselo al modal del Scenario 05. Sin esto habría que pedir el mismo
  // select dos veces, o el modal diría "movimientos en el depósito 6".
  const [ubicaciones, setUbicaciones] = useState([]);

  const [itemMovimientos, setItemMovimientos] = useState(null);

  const [permissions, setPermissions] = useState({
    ingresar: false,
    verMovimientos: false,
  });

  useEffect(() => {
    // Guarda de secuencia: el `useEffect` puede dejar DOS pedidos en vuelo y sin esto pinta el que
    // responda último, no el último que se pidió. Pasa en cada mount con filtros persistidos: el
    // efecto arranca con el query por defecto (`filterQuery` se inicializa sin leer localStorage) y
    // apenas el panel se hidrata dispara `onApply`, que cambia las deps y lanza el segundo. La
    // respuesta del primero puede llegar después y sobreescribir la buena, dejando la grilla
    // mostrando datos que NO son los del filtro que muestran los controles.
    // El cleanup corre cuando cambian las deps, así que marca vencido todo pedido anterior. Cubre
    // también las carreras que no son del mount: dos clicks rápidos en Buscar, paginar y filtrar
    // encimados.
    let vigente = true;

    setLoading(true);
    if (
      Security.hasPermission('INVENTARIO_STOCK_LISTAR') &&
      ConfigBusiness.get('inventarioItems.habilitado') === 'true'
    ) {
      securityPermission();
      fetchRows(() => vigente);
    } else {
      setRedirectTo('/error');
    }
    return () => {
      vigente = false;
    };
  }, [pagination, filterQuery, sortQuery]);

  const securityPermission = () => {
    setPermissions({
      // Scenario 04: el link al formulario de ingreso solo tiene sentido para quien puede cargarlo.
      // Es el mismo permiso que gatea la acción "Cargar stock" del catálogo.
      ingresar: Security.hasPermission('INVENTARIO_STOCK_INGRESAR'),
      // Scenario 05: ver el historial es un permiso APARTE de listar el stock, porque expone costos
      // y responsables. Ver existencias y ver quién las movió son dos audiencias distintas.
      verMovimientos: Security.hasPermission('INVENTARIO_STOCK_VISUALIZAR_MOVIMIENTOS'),
    });
  };

  /** `sigueVigente` lo provee el efecto que disparó el pedido. Ver la guarda de secuencia. */
  const fetchRows = (sigueVigente) => {
    Utils.getData(`${BASE_URL}/grid${getQuery()}`)
      .then((data) => {
        if (!sigueVigente()) {
          return; // llegó una respuesta vencida: la pidió un filtro que ya no es el vigente
        }
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setLoading(false);
      })
      .catch(() => {
        if (!sigueVigente()) {
          return;
        }
        setLoading(false);
      });
  };

  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;

    // La ubicación viaja COMPLETA o no viaja: con el tipo presente y el id vacío el backend
    // devuelve 400 (ItemStockService::normalizarUbicacion), así que sin depósito elegido no se
    // manda ninguna de las dos claves y la respuesta es el consolidado.
    const ubicacion = parsear(filterQuery.ubicacionId);

    if (ubicacion) {
      query += '&ubicacionType=' + encodeURIComponent(ubicacion.type);
      query += '&ubicacionId=' + encodeURIComponent(ubicacion.id);
    }

    // VEC-3781 Sc. 04 — las DOS claves del filtro de Producto/Repuesto, que el panel serializa
    // solo: `item` con los ids de los chips ("13,39") e `itemBusqueda` con el texto que quedó sin
    // resolver. Se SUMAN a los tres de abajo, que siguen igual.
    query += '&item=' + encodeURIComponent(filterQuery.item || '');
    query += '&itemBusqueda=' + encodeURIComponent(filterQuery.itemBusqueda || '');

    query += '&numeroDeParte=' + encodeURIComponent(filterQuery.numeroDeParte || '');
    query += '&externalCode=' + encodeURIComponent(filterQuery.externalCode || '');
    query += '&descripcion=' + encodeURIComponent(filterQuery.descripcion || '');

    Object.keys(SORT_QUERY_INIT).forEach((key) => {
      if (sortQuery[key]) query += '&' + key + 'Sort=' + sortQuery[key];
    });

    return query;
  };

  /**
   * Nombre de la ubicación filtrada, para el encabezado del modal del Scenario 05. Vacío =
   * consolidado. Se resuelve contra las opciones que ya cargó el panel; si todavía no llegaron, el
   * modal muestra el alcance sin nombrarlo en vez de mostrar el valor crudo — que desde VEC-3316
   * además sería "almacen:6" y no un id.
   */
  const nombreDeLaUbicacionFiltrada = () => etiquetaDeUbicacion(ubicaciones, filterQuery.ubicacionId);

  /**
   * El par ya partido, para el modal del Scenario 05. Se deriva una sola vez por render en vez de
   * llamar a `parsear()` dos veces dentro del JSX: son dos props del mismo valor y separarlas
   * abriría la puerta a que una quede y la otra no, que es justo el par a medias que el backend
   * rechaza con 400.
   */
  const ubicacionFiltrada = parsear(filterQuery.ubicacionId);

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  const requestSort = (key) => {
    let query = { ...sortQuery };
    let aux = sortQuery[key];
    Object.keys(SORT_QUERY_INIT).forEach((k) => {
      query[k] = '';
    });
    if (!aux) query[key] = 'asc';
    else if (aux === 'desc') query[key] = 'asc';
    else query[key] = 'desc';
    setPagination({ ...pagination, page: 0 });
    setSortQuery(query);
  };

  // LIMPIAR LA BÚSQUEDA — es lo que hace la "✕" de la franja de resultados. Devuelve los filtros al
  // default y oculta la franja. NO toca el ordenamiento: eso es del reset total.
  // Ver docs/design-system/grid-filter-panel.md ("Dos resets, no uno").
  const handleSearchClear = () => {
    setPagination({ ...PAGINATION_INIT });
    setFilterQuery(buildFilterQueryInit());
    setSearchActive(false);
    setFilterResetInputs(true);
  };

  // RESET TOTAL — el botón "Restaurar" del toolbar: además de limpiar la búsqueda, devuelve el
  // ordenamiento por defecto y cierra el panel. Reusa el parcial para no duplicar el reset; React
  // batchea los setState del handler, así que sigue habiendo un solo fetch.
  const handleFilterReset = () => {
    setSortQuery({ ...SORT_QUERY_INIT });
    setDropdownActive(false);
    handleSearchClear();
  };

  /**
   * Lo que aplica el panel: llega ya serializado a primitivos, con una clave por campo declarado.
   * La franja verde se muestra cuando hay algo cargado — esta grilla no tiene filtros por defecto,
   * así que alcanza con que alguna clave no esté vacía.
   */
  const handleApplyFilters = (query) => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setFilterQuery(query);
    setSearchActive(Object.keys(query).some((k) => query[k] !== '' && query[k] !== undefined));
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-content collpase show">
              <div className="card-body card-dashboard">
                <div className="container-fluid">
                  {/* El toolbar necesita TRES `col` de igual ancho: con col-6 + col + col-6 se pasa
                      de las 12 columnas de Bootstrap y el bloque de la derecha se va a la línea
                      siguiente. Ver grid-filter-panel.md. */}
                  <div className="row dt-icons">
                    <div className="col">
                      {/* El + NO crea una fila de stock: abre el ingreso (VEC-3314 Sc. 01), que es
                          lo único que hace existir stock. Va SIN `?item=` —el ítem se elige en el
                          formulario— y con `origen=stock` para que Guardar y Cancelar vuelvan acá y
                          no al catálogo. La acción de la fila sigue existiendo y manda el ítem
                          preseleccionado: son dos entradas al mismo formulario, no dos formularios. */}
                      {permissions.ingresar && (
                        <StyledTooltip
                          title={intl.formatMessage({ id: 'Nuevo', defaultMessage: 'Nuevo' })}
                          arrow
                          placement="right"
                        >
                          <div
                            className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                            onClick={() => setRedirectTo(props.match.url + '/ingresos/nuevo?origen=stock')}
                          >
                            <i className="ft-plus"></i>
                          </div>
                        </StyledTooltip>
                      )}
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        onClick={() => setDropdownActive(!dropdownActive)}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </div>
                    <div className="col">
                      <GridSearchResultsBadge
                        visible={searchActive}
                        mostrados={rows.length}
                        total={count}
                        onReset={() => handleSearchClear()}
                      />
                    </div>
                    <div className="col">
                      <GridToolbar>
                        <GridToolbarButton
                          messageId="inventarioStock.grid.tools_restore_filters.title"
                          label="Restaurar filtros y ordenamiento"
                          onClick={() => handleFilterReset()}
                        >
                          <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                        </GridToolbarButton>
                      </GridToolbar>
                    </div>
                  </div>
                </div>

                <InventarioStockGridFiltros
                  open={dropdownActive}
                  loading={loading}
                  resetSignal={filterResetInputs}
                  onResetSignalHandled={() => setFilterResetInputs(false)}
                  onApply={handleApplyFilters}
                  onOptionsLoaded={(opciones) => setUbicaciones(opciones.ubicacionId || [])}
                />

                <TableContainer>
                  <Table aria-label="inventario stock table" className="table-hover">
                    <TableHead>
                      <TableRow>
                        <SortableCell
                          field="numeroDeParte"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_numero_de_parte"
                          label="Nº de parte"
                        />
                        <SortableCell
                          field="descripcion"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_descripcion"
                          label="Descripción"
                        />
                        <SortableCell
                          field="categoria"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_categoria"
                          label="Categoría"
                        />
                        <SortableCell
                          field="subcategoria"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_subcategoria"
                          label="Subcategoría"
                        />
                        <SortableCell
                          field="marca"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_marca"
                          label="Marca"
                        />
                        {/* La unidad es parte de la IDENTIDAD del ítem, no un dato suelto: los tres
                            conteos son envases de ESA unidad, así que sin la columna un "8" no se
                            puede leer. No es ordenable: el endpoint no la tiene en su whitelist. */}
                        <SortableCell
                          field="unidad"
                          messageId="inventarioStock.grid.column_unidad"
                          label="Unidad"
                        />
                        <SortableCell
                          field="stockFisico"
                          align="right"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_stock_fisico"
                          label="Físico"
                        />
                        <SortableCell
                          field="stockReservado"
                          align="right"
                          sort={sortQuery}
                          onSort={requestSort}
                        >
                          <StyledTooltip
                            title={intl.formatMessage({
                              id: 'inventarioStock.grid.column_stock_reservado_ayuda',
                              defaultMessage:
                                'Unidades de stock comprometidas para un ticket de mantenimiento. Siguen contando en Físico, porque reservar no las mueve del depósito: lo que baja es Disponible.',
                            })}
                            arrow
                            placement="top"
                          >
                            <span>
                              {intl.formatMessage({
                                id: 'inventarioStock.grid.column_stock_reservado',
                                defaultMessage: 'Reservado',
                              })}
                            </span>
                          </StyledTooltip>
                        </SortableCell>
                        <SortableCell
                          field="stockUtilizado"
                          align="right"
                          sort={sortQuery}
                          onSort={requestSort}
                        >
                          <StyledTooltip
                            title={intl.formatMessage({
                              id: 'inventarioStock.grid.column_stock_utilizado_ayuda',
                              defaultMessage:
                                'Unidades que ya salieron del depósito al aprobarse el presupuesto. NO cuentan en Físico: sólo vuelven a Disponible si se libera la reserva o se cancela el ticket.',
                            })}
                            arrow
                            placement="top"
                          >
                            <span>
                              {intl.formatMessage({
                                id: 'inventarioStock.grid.column_stock_utilizado',
                                defaultMessage: 'Utilizado',
                              })}
                            </span>
                          </StyledTooltip>
                        </SortableCell>
                        <SortableCell
                          field="stockDisponible"
                          align="right"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_stock_disponible"
                          label="Disponible"
                        />
                        <SortableCell
                          field="stockMinimo"
                          align="right"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioStock.grid.column_stock_minimo"
                          label="Mínimo"
                        />
                        <SortableCell
                          field="estadoAlerta"
                          align="center"
                          messageId="inventarioStock.grid.column_estado"
                          label="Estado"
                        />
                        <SortableCell
                          field="acciones"
                          align="center"
                          style={{ width: '110px' }}
                          messageId="inventarioStock.grid.column_acciones"
                          label="Acciones"
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <GridLoadingRow visible={loading} columns={COLUMN_COUNT} />
                      {!loading &&
                        (rows.length > 0 ? (
                          rows.map((row) => (
                            <InventarioStockGridRow
                              intl={intl}
                              key={'GridRow_' + row.id}
                              baseUrl={props.match.url}
                              row={row}
                              permissions={permissions}
                              onVerMovimientos={setItemMovimientos}
                            />
                          ))
                        ) : (
                          <GridEmptyRow
                            columns={COLUMN_COUNT}
                            messageId="inventarioStock.grid.no_data_table"
                            label="No se encontraron resultados..."
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <GridPagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={count}
                  page={pagination.page}
                  perPage={pagination.perPage}
                  onPageChange={handleChangePage}
                  onPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scenario 05: el historial se abre para el ítem de la fila Y la ubicación que está
          filtrada. La resuelve la grilla, que es quien tiene el filtro aplicado.

          VEC-3316: el par sale de `parsear()`, así que el modal recibe el tipo REAL —almacén o
          base— y no la constante que había antes. Es lo que hace que el signo +/− del historial
          siga siendo correcto cuando lo filtrado es una base: `esIngreso` lo resuelve comparando
          el par completo contra los dos extremos del movimiento. */}
      <InventarioStockMovimientosModal
        item={itemMovimientos}
        ubicacionType={ubicacionFiltrada ? ubicacionFiltrada.type : null}
        ubicacionId={ubicacionFiltrada ? ubicacionFiltrada.id : null}
        ubicacionNombre={nombreDeLaUbicacionFiltrada()}
        onClose={() => setItemMovimientos(null)}
      />
    </>
  );
};

export default InventarioStockGrid;
